import {
  Typography,
  Box,
  Button,
  TablePagination,
  IconButton,
  Tooltip,
} from '@mui/material';
import LayoutBase from '../../shared/layouts';
import Cookies from 'js-cookie';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useEffect, useState } from 'react';
import { AxiosError, AxiosRequestConfig } from 'axios';
import { api } from '../../shared/services/api';
import { toast } from 'react-toastify';
import UndrawNoData from '../../assets/svg/undraw_no_data.svg';
import Loading from '../../components/loading';
import { formatCurrency, maskCPFCNPJ } from '../../shared/utils/mask';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import utc from 'dayjs/plugin/utc'; // <<< importa o plugin
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

dayjs.extend(utc); // <<< registra o plugin no dayjs

interface Column {
  id:
  | 'protocolo_interno'
  | 'protocolo_externo'
  | 'ambiente'
  | 'cliente_debitado'
  | 'cpf_cnpj_debitado'
  | 'conta_debitada'
  | 'agencia_debitado'
  | 'tipo_conta_debitada'
  | 'conta_destino'
  | 'valor_bloqueado'
  | 'descricao'
  | 'data';
  label: string;
  minWidth?: number;
  align?: 'right';
  format?: (value: number) => string;
}

export interface ReportItem {
  protocolo_interno: string;
  protocolo_externo: string;
  ambiente: string;
  cliente_debitado: string;
  cpf_cnpj_debitado: string;
  agencia_debitado: string;
  conta_debitada: string;
  tipo_conta_debitada: string;
  conta_destino: string;
  valor_bloqueado: string;
  descricao: string;
  data: string;
}

export interface ReportMeta {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface ReportResponse {
  data: ReportItem[];
  meta: ReportMeta;
}

const columns: readonly Column[] = [
  { id: 'protocolo_interno', label: 'Prot. Interno', minWidth: 70 },
  { id: 'protocolo_externo', label: 'Prot. Externo', minWidth: 140 },
  { id: 'ambiente', label: 'Ambiente', minWidth: 120 },
  { id: 'cliente_debitado', label: 'Cliente Debitado', minWidth: 170 },
  { id: 'cpf_cnpj_debitado', label: 'Doc. Debitado', minWidth: 100 },
  { id: 'conta_debitada', label: 'Conta Debitada', minWidth: 100 },
  { id: 'agencia_debitado', label: 'Agência Debitada', minWidth: 100 },
  { id: 'tipo_conta_debitada', label: 'Tp. Conta', minWidth: 70 },
  { id: 'conta_destino', label: 'Conta Destino', minWidth: 100 },
  { id: 'valor_bloqueado', label: 'Val. Bloqueado', minWidth: 170 },
  { id: 'descricao', label: 'Descrição', minWidth: 140 },
  { id: 'data', label: 'Data', minWidth: 70 },
];

const ReportBlocked = () => {
  const token = Cookies.get('@token');
  const [dataRows, setDataRows] = useState<ReportItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    // Só dispara a busca se já tiver feito uma busca anterior
    if (hasSearched) {
      formik.submitForm();
    }
  }, [page, rowsPerPage]);

  const formik = useFormik({
    initialValues: {
      data: null,
    },
    validationSchema: Yup.object({
      data: Yup.date()
        .nullable()
        .required('Data é obrigatória')
        .typeError('Data inválida'),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);

      if (!values.data) {
        toast.warn('Selecione uma data antes de buscar.');
        setIsLoading(false);
        return;
      }

      const formattedDate = dayjs(values.data).format('YYYY-MM-DD');

      const config: AxiosRequestConfig = {
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        params: {
          limit: rowsPerPage,
          page: page,
        },
      };

      try {
        const response = await api.get<ReportResponse>(
          `/externalLegacy/getReportMessage/${formattedDate}`,
          config,
        );

        const {
          data,
          meta: { totalItems },
        } = response.data;

        setDataRows(data || []);
        setTotalItems(totalItems || 1);
        setHasSearched(true);
      } catch (err) {
        const error = err as AxiosError<{ message: string }>; // faz o cast para AxiosError

        const apiMessage = error.response?.data?.message;
        setDataRows([]);
        setTotalItems(0);
        setHasSearched(false);
        toast.warning(` ${apiMessage || error.message || 'Erro desconhecido'}`);

        console.error('Erro completo:', error);
      } finally {
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    formik.submitForm();
  }, [page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  const handleDownloadPDF = () => {
    if (dataRows.length === 0) return;

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

    const dateStr = formik.values.data
      ? dayjs(formik.values.data).format('DD/MM/YYYY')
      : '';

    doc.setFontSize(14);
    doc.text(`Relatório da Remessa Bloqueio — ${dateStr}`, 14, 15);
    doc.setFontSize(9);
    doc.text(`Gerado em: ${dayjs().format('DD/MM/YYYY HH:mm')}`, 14, 21);

    const head = columns.map((col) => col.label);

    const body = dataRows.map((row) =>
      columns.map((col) => {
        const value = row[col.id];
        if (col.id === 'cpf_cnpj_debitado') return maskCPFCNPJ(value);
        if (col.id === 'data') return dayjs.utc(value).format('DD/MM/YYYY');
        if (col.id === 'valor_bloqueado') return formatCurrency(value);
        return value || '-';
      }),
    );

    autoTable(doc, {
      startY: 26,
      head: [head],
      body,
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [17, 92, 91], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { left: 8, right: 8 },
    });

    const fileName = `relatorio_bloqueio_${dateStr.replace(/\//g, '-') || 'sem-data'}.pdf`;
    doc.save(fileName);
  };

  return (
    <LayoutBase>
      <Typography color="textPrimary" variant="h5" gutterBottom>
        Relatório da remessa Bloqueio
      </Typography>
      <form onSubmit={formik.handleSubmit}>
        <Box display="flex" gap={2} mb={2} alignItems="flex-start">
          <Box display="flex" flexDirection="column">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Data"
                value={formik.values.data ? dayjs(formik.values.data) : null} // trata string vazia também
                onChange={(newValue) => {
                  if (
                    !newValue ||
                    newValue === null ||
                    !dayjs(newValue).isValid()
                  ) {
                    formik.setFieldValue('data', '');
                  } else {
                    formik.setFieldValue('data', dayjs(newValue).toISOString());
                  }
                }}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    name: 'data',
                    error: Boolean(formik.touched.data && formik.errors.data),
                    helperText: formik.touched.data && formik.errors.data,
                    onBlur: formik.handleBlur,
                    onKeyDown: (e) => {
                      if (e.key === 'Backspace') {
                        formik.setFieldValue('data', '');
                      }
                    },
                    sx: {
                      '& .MuiFormHelperText-root': {
                        margin: 0,
                        minHeight: '20px',
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>

            {/* {formik.touched.data && formik.errors.data && (
              <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                {formik.errors.data}
              </Box>
            )} */}
          </Box>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            sx={{ mt: '12px' }}
          >
            Buscar
          </Button>
          <IconButton
            onClick={() => {
              setDataRows([]);
              setHasSearched(false);
              setPage(1);
              setTotalItems(0);
              formik.setFieldValue('data', '');
            }}
            sx={{ mt: '12px' }}
          >
            <RefreshIcon />
          </IconButton>
          {dataRows.length > 0 && (
            <Tooltip title="Baixar PDF">
              <IconButton onClick={handleDownloadPDF} sx={{ mt: '12px' }} color="error">
                <PictureAsPdfIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </form>

      <Loading isLoading={isLoading} />

      {!hasSearched || (dataRows.length === 0 && !isLoading) ? (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Box
              component="img"
              src={UndrawNoData}
              alt="Svg em construção"
              sx={{
                maxWidth: '15%',
                height: 'auto',
                marginTop: 10,
              }}
            />
          </Box>
          <Typography textAlign={'center'} sx={{ marginTop: 2 }}>
            Relatório não encontrado, envie uma data valida...
          </Typography>
        </>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden', marginTop: 2 }}>
          <TableContainer sx={{ maxHeight: 650 }}>
            <Table aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {dataRows.map((row) => (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={Number(row.protocolo_interno)}
                  >
                    {columns.map((column) => {
                      const value = row[column.id];
                      return (
                        <TableCell key={column.id} align={column.align}>
                          {column.id === 'cpf_cnpj_debitado'
                            ? maskCPFCNPJ(value)
                            : column.id === 'data'
                              ? dayjs.utc(value).format('DD/MM/YYYY') // <<< usa .utc()
                              : column.id === 'valor_bloqueado'
                                ? formatCurrency(value)
                                : value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[15, 30, 50]}
            component="div"
            count={totalItems}
            rowsPerPage={rowsPerPage}
            page={page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
    </LayoutBase>
  );
};

export default ReportBlocked;
